import { useCallback, useEffect, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { RtcConfiguration, getWSCallServerURL, wsURL } from "../consts/webrtc";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store";
import { addMember, asyncAddMediaStreamTrack, removeMember, updateMediaStream } from "../redux/members";

export function useMemberManager() {
    const [peers, setPeers] = useState<Map<string, RTCPeerConnection>>(new Map<string, RTCPeerConnection>());
    const authInfo = useSelector((state: RootState) => state.auth.data);
    const localMember = useSelector((state: RootState) => state.members.data.find((member) => member.is_local === true));
    const [localIsVideoOn, setLocalIsVideoOn] = useState(localMember?.is_video_on ?? true);
    const [localIsAudioOn, setLocalIsAudioOn] = useState(localMember?.is_audio_on ?? true);
    const [localStream, setLocalStream] = useState<MediaStream>(localMember?.stream ?? new MediaStream());
    const members = useSelector((state: RootState) => state.members.data);
    const [connected, setConnected] = useState(false);
    const { sendMessage, lastMessage, readyState } = useWebSocket(getWSCallServerURL());
    const dispatch = useDispatch<AppDispatch>();
    const createPeerConnection = (toPubkey: string): RTCPeerConnection => {
        const peerConnection = new RTCPeerConnection(RtcConfiguration);
        peerConnection.onicecandidate = (event) => {
            if (event.candidate) {
                // Send candidate to pubkey
                const socketMessage: SocketMessage = {
                    type: "candidate",
                    room_id: authInfo.room_id,
                    pubkey: authInfo.pubkey,
                    to_pubkey: toPubkey,
                    data: JSON.stringify(event.candidate)
                }
                sendMessage(JSON.stringify(socketMessage))
            }
        }
        peerConnection.oniceconnectionstatechange = (event) => {
            if (peerConnection.iceConnectionState === "disconnected") {
                console.log("ice of", toPubkey, "disconnected")
            }
            if (peerConnection.iceConnectionState === "connected") {
                console.log("ice of", toPubkey, "connected")
            }
        }
        peerConnection.ontrack = (event) => {
            dispatch(asyncAddMediaStreamTrack({ pubkey: toPubkey, mediaStreamTrack: event.track }))
        }
        localStream.getTracks().forEach((track) => {
            peerConnection.addTrack(track, localMember!.stream!);
        })
        return peerConnection;
    }

    const getPeerConnection = (toPubkey: string): RTCPeerConnection | undefined => {
        return peers.get(toPubkey);
    }


    const createAnswerToPubkey = async (toPubkey: string, offerData: RTCSessionDescriptionInit) => {
        try {
            const peerConnection = createPeerConnection(toPubkey);
            peers.set(toPubkey, peerConnection);
            await peerConnection.setRemoteDescription(offerData);
            const answer = await peerConnection.createAnswer();
            await peerConnection.setLocalDescription(answer);
            const socketMessage: SocketMessage = {
                type: "answer",
                room_id: authInfo.room_id,
                pubkey: authInfo.pubkey,
                to_pubkey: toPubkey,
                data: JSON.stringify(answer)
            }
            sendMessage(JSON.stringify(socketMessage))
        } catch (e) {
            console.log("create answer error", e)
        }
    }

    const createOfferToPubkey = async (toPubkey: string) => {
        try {
            const peerConnection = createPeerConnection(toPubkey);
            peers.set(toPubkey, peerConnection);
            const offer = await peerConnection.createOffer();
            await peerConnection.setLocalDescription(offer);
            // Đoạn này được update async nên không chắc là map có được update ngay lập tức hay không
            // setPeers(new Map<string, RTCPeerConnection>(peers).set(toPubkey, peerConnection))
            const socketMessage: SocketMessage = {
                type: "offer",
                room_id: authInfo.room_id,
                pubkey: authInfo.pubkey,
                to_pubkey: toPubkey,
                data: JSON.stringify(offer)
            }
            sendMessage(JSON.stringify(socketMessage))
        } catch (e) {
            console.log("create offer error", e)
        }

    }

    useEffect(() => {
        if (localMember?.stream) {
            setLocalStream(localMember.stream);
        }
        if (localMember?.is_video_on !== localIsVideoOn || localMember?.is_audio_on !== localIsAudioOn) {
            setLocalIsAudioOn(localMember?.is_audio_on ?? true);
            setLocalIsVideoOn(localMember?.is_video_on ?? true);
            sendMessage(JSON.stringify({
                type: "update_media",
                room_id: authInfo.room_id,
                pubkey: authInfo.pubkey,
                is_video_on: localIsVideoOn,
                is_audio_on: localIsAudioOn
            }))
        }

    }, [localMember])
    useEffect(() => {
        if (readyState === ReadyState.OPEN && !connected && authInfo.pubkey !== "") {
            setConnected(true);
            const socketMessage: SocketMessage = {
                type: "join",
                room_id: authInfo.room_id,
                pubkey: authInfo.pubkey,
            }
            sendMessage(JSON.stringify(socketMessage))
        }
        if (readyState === ReadyState.CLOSED) {
            setConnected(false);
        }
        if (lastMessage) {
            const message: SocketMessage = JSON.parse(lastMessage.data)
            if (message.pubkey === authInfo.pubkey) {
                return;
            }
            if (message.type === "offer") {
                dispatch(addMember({ pubkey: message.pubkey, stream: new MediaStream(), is_video_on: message.is_video_on, is_audio_on: message.is_audio_on }))
                createAnswerToPubkey(message.pubkey!, JSON.parse(message.data!));
            }
            if (message.type === "answer") {
                try {
                    // Not sure to get peer connection becasue of async set State
                    const peer = getPeerConnection(message.pubkey!)
                    if (peer === undefined) {
                        setTimeout(async () => {
                            const peer = getPeerConnection(message.pubkey!);
                            if (peer === undefined) {
                                console.log("peer is still undefined of pubkey", message.pubkey)
                                return;
                            }
                            try {
                                await peer.setRemoteDescription(JSON.parse(message.data!));
                            } catch (e) {
                                console.log("set remote description error", e, "answer", message.data)
                            }
                        }, 500)
                        return
                    }
                    const data = JSON.parse(message.data!);
                    (async () => {
                        try {
                            await peer.setRemoteDescription(data);
                        } catch (e) {
                            console.log("set remote description error", e, "answer", message.data)
                        }
                    })();
                } catch (e) {
                    console.log("set remote description error", e, "answer", message.data)
                }

            }
            if (message.type === "candidate") {
                const data = JSON.parse(message.data!);
                const peer = getPeerConnection(message.pubkey!);
                if (peer === undefined) {
                    setTimeout(async () => {
                        const peer = getPeerConnection(message.pubkey!);
                        if (peer === undefined) {
                            console.log("peer is still undefined of pubkey", message.pubkey)
                            return;
                        }
                        try {
                            await peer.addIceCandidate(data);
                        } catch (e) {
                            console.log("add ice candidate error", e)
                        }
                    }, 1000)
                    return;
                }
                (async () => {
                    try {
                        await peer.addIceCandidate(data);
                    } catch (e) {
                        console.log("add ice candidate error", e)
                    }
                })();
            }
            if (message.type === "joined") {
                if (message.pubkey === authInfo.pubkey) {
                    return;
                }
                dispatch(addMember({ pubkey: message.pubkey, stream: new MediaStream(), is_video_on: message.is_video_on, is_audio_on: message.is_audio_on }))
                setTimeout(() => {
                    createOfferToPubkey(message.pubkey!);
                }, 500);
            }
            if (message.type === "left") {
                dispatch(removeMember({ pubkey: message.pubkey }))
                peers.get(message.pubkey!)?.close();
                peers.delete(message.pubkey!);
            }
            if (message.type === "update_media") {
                dispatch(updateMediaStream({ pubkey: message.pubkey, is_video_on: message.is_video_on, is_audio_on: message.is_audio_on }))
            }
        }

    }, [lastMessage, readyState, authInfo])
    return { members };
}